<script setup lang="ts">
import {computed} from "vue";
import {useModalStore} from "../../store/modal";

interface IClient {
    first_name: string,
    last_name: string,
    cell_number: string,
}

const modalStore = useModalStore()

const props = withDefaults(
    defineProps<{
        id: number,
        checked: boolean,
        client: IClient,
        unread_message_count: number | null,
        created_at: string,
        completed_at: string | null,
        completed_by_id: number | null,
        assignee: IClient | null,
        cell_number: string,
        bulk_message?: boolean,
        is_overdue?: boolean
    }>(), {
        completed_at: null,
        bulk_message: false,
        is_overdue: false
    })

const initials = computed(() => props.client.first_name[0] + (props.client.last_name[0] ?? ""))

const showActions = (props) => {
    modalStore.open("Chat", props, `#chat-row-${props.id}`, 'center-center')
}
const assign = () => {
    modalStore.open('AssignChat', {id: props.id}, 'body', 'bottom-right')
}
</script>

<template>
    <router-link :to="`/chats/view/${id}`" class="chat-row-item"
                 :class="{'--is-checked':checked, '--is-completed': completed_at, '--is-overdue': is_overdue && !completed_at}"
                 :title="is_overdue && !completed_at ? 'Client has been waiting over 30 minutes for a response.' : undefined"
                 :id="`chat-row-${id}`">
        <ah-icon v-if="!completed_at" class="chat-row-item-checkbox"
                 :name="checked ? `checkbox-selected` : `checkbox-deselected`"
                 @click.prevent="$emit('selectChat', id)"/>

        <ah-icon v-else name="check-all" color="var(--color-tiffany-dark)"/>
        <div class="chat-row-item-client">
            <div class="chat-row-item-client-initials">{{ initials }}</div>
            <div class="chat-row-item-client-unread" v-if="unread_message_count && unread_message_count > 0">
                {{ unread_message_count }}
            </div>
        </div>

        <div class="chat-row-item-date">
            <span>{{
                    new Date(created_at).toLocaleDateString("en-GB", {day: "2-digit", month: "short", year: "numeric"})
                }}</span>
            <span>{{
                    new Date(created_at).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false
                    })
                }}</span>
        </div>

        <p class="chat-row-item-name">
            <strong>Name.</strong>
            {{ `${client.first_name} ${client.last_name}` }}
        </p>

        <p class="chat-row-item-name">
            <strong>Cell nr.</strong>
            {{ cell_number }}
        </p>

        <p class="chat-row-item-assignee" v-if="assignee">
            <strong>Assigned to.</strong>
            {{ `${assignee.first_name} ${assignee.last_name}` }}
        </p>

        <button @click.prevent="assign()" class="chat-row-item-assign-button" v-else>
            <span class="label">Assign</span>
            <ah-icon name="user-single-add"/>
        </button>

        <div class="chat-row-item-bulk-message-wrapper">
            <span class="chat-row-item-bulk-message" v-if="bulk_message">Bulk</span>
        </div>

        <button v-if="!completed_at" @click.prevent="showActions({id, assignee})" class="chat-row-item-round-button">
            <ah-icon name="more-default"/>
        </button>

        <p class="chat-row-item-assignee" v-else>
            <strong>{{completed_by_id ? 'Completed on.' : 'Auto Completed on.'}}</strong>
            {{
                `${new Date(completed_at).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric"
                })} @ ${new Date(completed_at).toLocaleTimeString("en-GB", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false
                })}`
            }}
        </p>

        <button class="chat-row-item-round-button">
            <ah-icon name="arrow-right"/>
        </button>
    </router-link>
</template>

<style scoped lang="scss">
.chat-row-item {
    padding: 1rem 2rem;
    display: grid;
    grid-template-columns: 2.4rem 6.1rem 12rem 1fr 1fr 1fr 12rem 3.6rem 3.6rem;
    gap: 2rem;
    background: var(--color-white);
    border-radius: 2.4rem;
    align-items: center;
    border: .2rem solid transparent;
    position: relative;

    &-checkbox {
        fill: var(--color-hazy);
        cursor: pointer;
    }

    &-client {
        position: relative;

        &-initials {
            width: 4.8rem;
            height: 4.8rem;
            background: var(--color-tiffany-dark);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: var(--color-white);
            @include font(1.6, 1.9, black);
        }

        &-unread {
            width: 2.9rem;
            height: 2.9rem;
            background: var(--color-raspberry);
            @include font(1.3, 1.3, black);
            color: var(--color-white);
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            position: absolute;
            top: 50%;
            right: 0;
            transform: translateY(-50%);
            border: .4rem solid var(--color-white);
        }
    }

    &-date {
        height: 6rem;
        background: var(--color-mist);
        border: .1rem solid var(--color-dew);
        border-radius: 1.8rem;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: .6rem;

        span {
            @include font(1.2, 1.2, black);
            color: var(--color-thunder);
        }
    }

    p {
        @include font(1.2, 1.2, book);
        display: flex;
        flex-direction: column;
        gap: .6rem;

        strong {
            @include font(1.4, 1.4, black);
        }
    }

    button {
        border: none;

        &:hover {
            background: var(--color-raspberry) !important;
        }
    }

    &-bulk-message {
        display: block;
        text-transform: capitalize;
        background: var(--color-mist);
        border: .1rem solid var(--color-dew);
        text-align: center;
        @include font(1.1, 3, black);
        border-radius: 1.5rem;
    }

    &-assign-button {
        background: var(--color-tiffany);
        max-width: 18rem;
        padding: 1rem;
        border-radius: 2.4rem;
        display: flex;
        gap: 1rem;
        align-items: center;
        justify-content: center;

        span {
            @include font(1.4, 1.4, black);
            color: var(--color-white);
        }

        .icon {
            fill: var(--color-white);
        }
    }

    &-round-button {
        border-radius: 50%;
        background: var(--color-white);
        width: 3.6rem;
        height: 3.6rem;
        display: flex;
        align-items: center;
        justify-content: center;

        .icon {
            fill: var(--color-hazy);
        }

        &:hover {
            .icon {
                fill: var(--color-white);
            }
        }
    }

    &.--is-completed {
        grid-template-columns: 2.4rem 6.1rem 12rem 1fr 1fr 1fr 12rem 1fr 3.6rem;
    }

    &.--is-overdue {
        background: rgba(196, 18, 64, .15);
        border-color: var(--color-red-raspberry);

        .chat-row-item-client {
            &-unread {
                border-color: #f3d0d9;
            }
        }
    }

    &.--is-checked {
        background: rgba(0, 153, 145, .2);
        border-color: var(--color-tiffany-dark);

        .chat-row-item-client {
            &-unread {
                border-color: #cce1e2;
            }
        }
    }

    @include dark() {
        background: var(--color-darth-maul);

        &-client {
            &-initials {
                color: var(--color-darth-vader);
            }

            &-unread {
                border-color: var(--color-darth-maul);
            }
        }

        &-date {
            background: var(--color-black-50);
            border-color: var(--color-black-50);

            * {
                color: var(--color-white);
            }
        }

        &-bulk-message {
            background: var(--color-black-50);
            border-color: transparent;
            color: var(--color-white);
        }

        &-round-button {
            background: var(--color-darth-maul);
        }

        &.--is-overdue {
            background: rgba(196, 18, 64, .35);

            .chat-row-item-client-unread {
                border-color: transparent;
            }
        }
    }

    @include hover {
        background: var(--color-tiffany-dark);

        .icon {
            fill: var(--color-white);
        }

        .chat-row-item-client {
            &-initials {
                background: var(--color-white);
                color: var(--color-darth-vader);
            }

            &-unread {
                border-color: var(--color-tiffany-dark);
            }
        }

        .chat-row-item-date {
            background: var(--color-black-50);
            border-color: transparent;

            span {
                color: var(--color-white);
            }
        }

        p {
            color: var(--color-white);
        }

        .chat-row-item-bulk-message {
            background: var(--color-black-50);
            border-color: transparent;
            color: var(--color-white);
        }

        .chat-row-item-round-button {
            background: var(--color-tiffany-dark);
        }
    }
}

</style>
